/**
 * ComparisonOnlyDisabledPanel.tsx
 *
 * §38.7 — comparisonOnly Disabled Panel State (YoY Slice 3.1, Option B — CEO-locked 2026-05-27).
 *
 * Renders a panel shell in the visible-disabled state when a `comparisonOnly: true`
 * panel is present on the dashboard but the season filter has transitioned to 1 season.
 *
 * Visual treatment per mockup D (Option B):
 *   - Card shell: 2px dashed border using border-border token (NOT an error/red treatment).
 *   - Panel data area: opacity-[0.35], pointer-events-none (ghost of the last-rendered data).
 *   - Disabled callout: full opacity, muted background, Info icon + "Requires 2+ seasons."
 *   - Restore button: `[+ Add YYYY]` — fires AI chat-confirmed mutation via `onRestore` callback.
 *
 * CRITICAL — CEO axiom 2026-05-27 (feedback_no_direct_mutation_buttons_post_dashboard_save):
 *   The `[+ Add YYYY]` button does NOT directly dispatch a season filter mutation.
 *   It calls `onRestore()` which fires `sendEditChatMessageThunk` with a natural-language
 *   message (`"Compare to YYYY"`) — the BE stages `pendingMutation`, FSM moves to
 *   `awaiting_confirmation`, and the user confirms in chat. This is the only
 *   permitted mutation path for a saved dashboard.
 *
 * Layout:
 *   The component renders INSTEAD OF the normal panel card when the
 *   `COMPARISON_ONLY_DISABLED_ONE_SEASON` warning is detected. It is NOT a
 *   wrapper added around PanelRenderer output — PanelRenderer itself branches
 *   early when the warning is present and delegates to this component.
 *
 * Sibling concern:
 *   This component is intentionally SEPARATE from PanelWarningCalloutGroup.
 *   PanelWarningCalloutGroup renders informational callouts INSIDE an otherwise
 *   healthy panel. This component replaces the entire card for the disabled case.
 *
 * Brand guide §38.7 / §34.1 / §29.
 * YoY TDD §3.5.
 */

import React from 'react'
import { Info, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { PanelAggregatedWarning } from '@/types/DashboardV1.types'

// ─── ComparisonOnlyDisabledPanel ─────────────────────────────────────────────

interface ComparisonOnlyDisabledPanelProps {
  /** Panel title rendered at full opacity in the card header. */
  title: string
  /**
   * The COMPARISON_ONLY_DISABLED_ONE_SEASON warning. Used to derive the
   * `priorSeason` for the `[+ Add YYYY]` button label.
   *
   * `warning.context.priorSeason` — the year to restore (e.g. `'2024'`).
   * Fallback: `String(new Date().getFullYear() - 1)` when absent.
   */
  warning: PanelAggregatedWarning
  /**
   * Ghost content of the panel (the last-rendered data view from `PanelRenderer`).
   * Rendered at 35% opacity below the title, above the callout.
   *
   * Must be provided as a React node so the dimmed area accurately reflects
   * what the panel looked like with 2+ seasons — matching the §38.7 visual
   * intent of "ghost of what was there."
   */
  ghostContent: React.ReactNode
  /**
   * Called when the user clicks `[+ Add YYYY]`.
   *
   * MUST route through AI chat-confirmed mutation (sendEditChatMessageThunk).
   * Callers MUST NOT dispatch a direct filter mutation from this callback.
   *
   * The parent (Canvas.tsx → PanelCanvasV1 → PanelRenderer) is responsible
   * for creating this handler and ensuring it calls:
   *   `dispatch(sendEditChatMessageThunk(token, 'Compare to YYYY'))`
   *
   * When absent (e.g. edit session not yet bootstrapped), the restore button
   * still renders but is disabled so the user can see the affordance while the
   * session bootstraps.
   */
  onRestore?: () => void
  /** Additional CSS classes on the outer Card root. */
  className?: string
}

// ─── Shared class constant ────────────────────────────────────────────────────

const TITLE_CLASS = 'text-xs font-semibold text-secondary uppercase tracking-widest'

// ─── ComparisonOnlyDisabledPanel ─────────────────────────────────────────────

/**
 * Visible-disabled card shell for a `comparisonOnly: true` panel when the
 * dashboard has been filtered to 1 season.
 *
 * Accessibility:
 *   - `role="region"` with `aria-label` identifies the panel as disabled.
 *   - `aria-disabled="true"` on the Card signals the unavailable state.
 *   - Dimmed ghost content is `aria-hidden="true"` — it conveys no
 *     information screen readers need to re-read (the callout covers it).
 *   - Restore button has a unique `aria-label` describing the action.
 *
 * Visual contract (§38.7 Option B):
 *   - Border: `border-dashed border-2 border-border` — neutral dashed, NOT
 *     `border-destructive` (this is a capability gate, not an error).
 *   - Ghost content: `opacity-[0.35] pointer-events-none` — 35% per mockup.
 *   - Callout: `bg-muted border border-border rounded-lg` — muted background.
 *   - Button: `bg-accent-background border border-accent/50 text-accent` —
 *     accent-tinted pill per mockup `btn-add-season`.
 */
export const ComparisonOnlyDisabledPanel: React.FC<ComparisonOnlyDisabledPanelProps> = ({
  title,
  warning,
  ghostContent,
  onRestore,
  className,
}) => {
  // Derive the prior season from warning context.
  // `priorSeason` is populated by the BE in the COMPARISON_ONLY_DISABLED_ONE_SEASON
  // warning context (YoY Slice 3.1 BE). Fallback to calendar year - 1 for
  // robustness while the BE is in-flight (Slice 3.1 FE ships before BE is merged).
  const priorSeason =
    warning.context?.priorSeason ??
    String(new Date().getFullYear() - 1)

  const restoreButtonLabel = `Add ${priorSeason} to season filter to restore this panel`

  return (
    <Card
      className={cn(
        // §38.7: dashed 2px border at full opacity — signals "unavailable", not "error"
        'rounded-xl border-2 border-dashed border-border bg-card shadow-sm',
        className,
      )}
      role="region"
      aria-label={`${title} — disabled, requires 2 seasons`}
      aria-disabled="true"
      data-testid="comparison-only-disabled-panel"
    >
      {/* Title — full opacity, muted foreground color (capability gate signal) */}
      <CardHeader className="p-5 pb-2">
        <CardTitle className={cn(TITLE_CLASS, 'text-muted-foreground')}>
          {title}
        </CardTitle>
      </CardHeader>

      <CardContent className="px-5 pb-4 pt-0">
        {/*
          Ghost content — dimmed to 35% opacity, non-interactive.
          `aria-hidden` prevents screen readers from re-announcing data
          that is logically unavailable (the callout explains the state instead).
        */}
        <div
          className="opacity-[0.35] pointer-events-none"
          aria-hidden="true"
          data-testid="comparison-only-ghost-content"
        >
          {ghostContent}
        </div>

        {/*
          Disabled callout — full opacity.
          role="note": this is a system-state explanation, not an error.
          WCAG AA: #64748B on #F8FAFC = 4.76:1 contrast (brand guide §38.5 note).
        */}
        <div
          className={cn(
            'mt-3 p-3 bg-muted border border-border rounded-lg',
            'flex flex-col items-start gap-2',
          )}
          role="note"
          aria-label={`${title} panel requires 2 or more seasons to display`}
          data-testid="comparison-only-callout"
        >
          {/* Info icon + copy row */}
          <div className="flex items-center gap-1.5">
            <Info
              size={12}
              aria-hidden="true"
              className="text-muted-foreground flex-shrink-0"
            />
            <p
              className="text-muted-foreground font-medium leading-snug"
              style={{ fontSize: '11px' }}
            >
              Requires 2+ seasons.
            </p>
          </div>

          {/*
            [+ Add YYYY] restore button.

            CEO axiom (2026-05-27): fires AI chat-confirmed mutation via `onRestore`.
            The button itself does NOT call dispatch(setSeasonFilter(...)). It calls
            onRestore() which is bound in Canvas.tsx to:
              dispatch(sendEditChatMessageThunk(token, `Compare to ${priorSeason}`))

            When `onRestore` is absent (edit session not yet bootstrapped), the
            button is rendered disabled so the affordance is visible while the session
            opens. This matches the pattern used by the Retry button in PartialFailureCallout.
          */}
          <button
            type="button"
            onClick={onRestore}
            disabled={!onRestore}
            className={cn(
              'inline-flex items-center gap-1',
              'px-2.5 py-1',
              'bg-accent-background border border-accent/50 rounded-md',
              'text-accent font-semibold',
              'cursor-pointer',
              'transition-colors duration-100',
              'hover:bg-accent/10 hover:border-accent',
              'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
            style={{ fontSize: '10.5px' }}
            aria-label={restoreButtonLabel}
            data-testid="comparison-only-restore-button"
          >
            <Plus size={10} aria-hidden="true" />
            Add {priorSeason}
          </button>
        </div>
      </CardContent>
    </Card>
  )
}
